import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';

import { getLocaleInURLFormat, disableScroll, enableScroll } from 'utils';

import { withSeoData } from 'hocs';

import Table from 'components/ConvertorTable';
import CopyButton from 'components/CopyButton';
import { SectionTitle } from 'components/SectionTitle';
import BanksSelect from './BanksSelect';

import {
  PageContentSection,
  BankLink,
  BankAmount,
  AddBankButton,
  StyledModal,
} from './styles';

StyledModal.setAppElement('#root');

class AllBanksTable extends PureComponent {
  static getDerivedStateFromProps(nextProps, prevState) {
    if (nextProps.banks !== prevState.banks) {
      return {
        banks: nextProps.banks,
        banksToShow: nextProps.banks
          .map(bank => bank.id)
          .filter((bankId, index) => index < 10),
      };
    }

    return null;
  }

  columnsIds = ['bank', 'rate', 'amount'];

  modalStyles = {
    overlay: {
      zIndex: 3000000001,
      backgroundColor: 'rgba(0,0,0,0.2)',
    },
  };

  constructor(props) {
    super(props);
    this.state = {
      sortOrder: 'asc',
      showBanksSelect: false,
      banks: null,
      banksToShow: [],
    };
  }

  handleAddBankBtnClick = () => {
    this.setState(() => ({
      showBanksSelect: true,
    }));
  };

  handleBanksSelectChange = banksToShow => {
    enableScroll();

    this.setState(() => ({
      banksToShow,
      showBanksSelect: false,
    }));
  };

  handleBankSelectClose = () => {
    enableScroll();

    this.setState(() => ({
      showBanksSelect: false,
    }));
  };

  getFormattedSourceValue = () => {
    const { sourceValue, sourceType, intl } = this.props;

    return intl.formatNumber(sourceValue, {
      maximumFractionDigits: sourceType === 'crypto' ? 8 : 2,
    });
  };

  getBankById = bankId => {
    const { banks } = this.props;

    return banks.find(bank => bank.id === bankId);
  };

  getTextForCopy = ({ summ, nameG, timestamp }) => {
    const { intl, source, target } = this.props;

    const sourceLabel = intl.formatMessage({
      id: `tables.source.label`,
    });

    const bankNameLabel = intl.formatMessage(
      {
        id: `allBanks.byRate`,
      },
      { bank: nameG }
    );

    const sourceLink = `https://${process.env.APP_DOMAIN}${getLocaleInURLFormat(
      intl.locale
    )}${process.env.APP_ASSETS_PUBLIC_PATH}`;

    const formattedSourceValue = this.getFormattedSourceValue();

    return `${formattedSourceValue} ${this.getCurrencyNameForCopy(
      source
    )} = ${summ} ${this.getCurrencyNameForCopy(
      target
    )} ${bankNameLabel} за ${timestamp}. ${sourceLabel}: ${sourceLink}`;
  };

  getCurrencyNameForCopy = ({ nameGp, name, code }) => {
    return nameGp || (name && name.toLowerCase()) || code;
  };

  getColumnsConfig = () => {
    const { intl, target, source } = this.props;

    const formattedSourceValue = this.getFormattedSourceValue();

    return this.columnsIds.map(id => {
      if (id === 'amount') {
        return {
          id,
          children: `${formattedSourceValue} ${source.code} в ${target.code}`,
        };
      }

      return {
        id,
        children:
          id !== 'bank'
            ? intl.formatMessage(
                {
                  id: `allBanks.${id}.col.title`,
                },
                { currency: target.code }
              )
            : null,
      };
    });
  };

  getRowsConfig = () => {
    const { target, intl } = this.props;
    const { banksToShow, sortOrder } = this.state;

    return banksToShow
      .reduce((result, bankId) => {
        const bank = this.getBankById(bankId);

        if (!bank) {
          return result;
        }

        return [...result, bank];
      }, [])
      .sort(({ rate: rateA }, { rate: rateB }) =>
        sortOrder === 'asc' ? rateA - rateB || 1 : rateB - rateA || 1
      )
      .map(({ id, name, nameG, rate, summ, slug, type, timestamp }) => {
        const roundedSumm = intl.formatNumber(summ, {
          maximumFractionDigits: type === 'crypto' ? 8 : 2,
        });

        const companyLink = `https://${
          process.env.APP_DOMAIN
        }${getLocaleInURLFormat(intl.locale)}/company/${slug}/`;

        const textForCopy = this.getTextForCopy({
          summ: roundedSumm,
          nameG,
          timestamp,
        });

        return {
          id,
          ceils: [
            {
              key: `bank-${id}`,
              children: (
                <BankLink name={name} href={companyLink} target="_blank" />
              ),
              props: {
                style: {
                  width: '40%',
                },
              },
            },

            {
              key: `rate-${id}`,
              children: rate ? intl.formatNumber(rate) : '-',
            },

            {
              key: `amount-${id}`,
              children: (
                <BankAmount>
                  <CopyButton
                    textForCopy={textForCopy}
                    summ={summ ? roundedSumm : '-'}
                    currency={target.code}
                  />
                </BankAmount>
              ),
            },
          ],
        };
      });
  };

  render() {
    const { intl, banks, allBanksTitle } = this.props;
    const { banksToShow, showBanksSelect } = this.state;

    if (!banks.length) {
      return null;
    }

    return (
      <PageContentSection as="section">
        <SectionTitle>{allBanksTitle}</SectionTitle>
        <Table columns={this.getColumnsConfig()} rows={this.getRowsConfig()} />
        <AddBankButton icon="pencil" onClick={this.handleAddBankBtnClick}>
          {intl.formatMessage({ id: 'editBankList' })}
        </AddBankButton>
        <StyledModal
          isOpen={showBanksSelect}
          onRequestClose={this.handleBankSelectClose}
          onAfterOpen={disableScroll}
          style={this.modalStyles}
        >
          {showBanksSelect && (
            <BanksSelect
              banks={banks}
              selectedBanksIds={banksToShow}
              onRequestReset={this.handleBankSelectClose}
              onRequestChange={this.handleBanksSelectChange}
            />
          )}
        </StyledModal>
      </PageContentSection>
    );
  }
}

AllBanksTable.propTypes = {
  banks: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      rate: PropTypes.number,
      summ: PropTypes.number,
      type: PropTypes.string,
      name: PropTypes.string,
      nameG: PropTypes.string,
      logo: PropTypes.string,
      slug: PropTypes.string,
    })
  ).isRequired,
  sourceValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  sourceType: PropTypes.string.isRequired,
  source: PropTypes.objectOf(PropTypes.string).isRequired,
  target: PropTypes.objectOf(PropTypes.string).isRequired,

  intl: PropTypes.shape({
    locale: PropTypes.string,
    formatMessage: PropTypes.func,
    formatNumber: PropTypes.func,
  }).isRequired,

  allBanksTitle: PropTypes.string.isRequired,
};

export default withSeoData(AllBanksTable);
